import { BarChart3, Clock, FileDown, MapPin, Users } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { obter, paraQuery } from '../api';
import { Aviso, Carregando, Erro, Vazio } from '../componentes/base';
import { GrelhaKpi, Kpi } from '../componentes/Kpi';
import { BarrasProporcao, GraficoEvolucao, GraficoHorasPorDia, Painel } from '../componentes/graficos';
import { exportarDashboardPdf } from '../exportar';
import { dataCurta, euros, horas, km, mesPorExtenso, numero } from '../formatos';
import type { DadosDashboard } from '../tipos';

export default function Dashboard() {
  // Empty on first load so the server picks the latest month that has data.
  // Defaulting to today would open on an empty month, which is the normal
  // case: timesheets arrive after the month they cover.
  const [mes, setMes] = useState('');
  const [dados, setDados] = useState<DadosDashboard | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aCarregar, setACarregar] = useState(true);

  const carregar = useCallback(() => {
    setACarregar(true);
    setErro(null);
    obter<DadosDashboard>(`/dashboard${paraQuery({ mes })}`)
      .then((d) => { setDados(d); setMes(d.mes); })
      .catch((e) => setErro(e.message))
      .finally(() => setACarregar(false));
  }, [mes]);

  useEffect(carregar, [carregar]);

  if (aCarregar && !dados) return <Carregando />;
  if (erro) return <Erro mensagem={erro} aoTentar={carregar} />;
  if (!dados) return null;

  const { totais, variacao } = dados;
  const semDados = totais.n_registos === 0;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <BarChart3 className="h-6 w-6 text-marca-600 dark:text-marca-400" aria-hidden />
          <div>
            <h1 className="text-xl font-semibold">Dashboard</h1>
            <p className="text-sm text-tinta-500 dark:text-tinta-400">{mesPorExtenso(dados.mes)}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 sem-impressao">
          <label htmlFor="mes" className="sr-only">Mês</label>
          <input
            id="mes" type="month" className="campo w-auto" value={mes}
            onChange={(e) => e.target.value && setMes(e.target.value)}
          />
          <button
            type="button" className="btn bg-red-600 text-white hover:bg-red-700"
            disabled={semDados}
            onClick={() => exportarDashboardPdf(dados)}
          >
            <FileDown className="h-4 w-4" aria-hidden />Relatório PDF
          </button>
        </div>
      </div>

      {dados.alertas.map((alerta) => (
        <Aviso key={alerta.tipo}>
          {alerta.mensagem}{' '}
          <Link to="/funcionarios" className="font-medium underline">Ver funcionários</Link>
        </Aviso>
      ))}

      {semDados ? (
        <div className="cartao">
          <Vazio
            titulo="Ainda não há registos neste mês"
            descricao="Assim que registar horas, os totais e os gráficos aparecem aqui."
            acao={<Link to="/registo-horas" className="btn-primario">Registar horas</Link>}
          />
        </div>
      ) : (
        <>
          <GrelhaKpi>
            <Kpi
              rotulo="Total de horas" valor={horas(totais.total_horas)}
              sub={`vs ${dados.mes_anterior}`} variacao={variacao.total_horas}
            />
            <Kpi
              rotulo="Serviços" valor={numero(totais.n_servicos)}
              sub={`${numero(totais.n_dias)} dias`} variacao={variacao.n_servicos}
            />
            <Kpi
              rotulo="Funcionários" valor={numero(totais.n_funcionarios)}
              sub={`${numero(totais.funcionarios_ativos)} ativos`}
            />
            <Kpi rotulo="Horas extra" valor={horas(totais.horas_extra)}
              sub={totais.total_horas ? `${((Number(totais.horas_extra) / totais.total_horas) * 100).toFixed(1)}% do total` : undefined} />
            <Kpi rotulo="Quilómetros" valor={km(totais.total_km)} />
            <Kpi
              rotulo="Custo estimado" valor={euros(totais.custo_total)}
              sub={`vs ${dados.mes_anterior}`} variacao={variacao.custo_total} inverterCor
            />
          </GrelhaKpi>

          <div className="grid gap-5 xl:grid-cols-2">
            <Painel titulo="Horas por dia" descricao={`Distribuição em ${mesPorExtenso(dados.mes)}`}>
              <GraficoHorasPorDia dados={dados.por_dia} />
            </Painel>

            <Painel titulo="Evolução de horas" descricao="Últimos 12 meses com registos">
              <GraficoEvolucao dados={dados.evolucao} />
            </Painel>
          </div>

          {/* items-start so a short panel is not stretched to match a tall one. */}
          <div className="grid items-start gap-5 xl:grid-cols-2">
            <Painel
              titulo="Horas por funcionário"
              descricao={`${dados.por_funcionario.length} pessoa(s) neste mês`}
              acao={<Link to="/folha-mensal" className="text-xs font-medium text-marca-600 hover:underline dark:text-marca-400">Folha mensal →</Link>}
            >
              <BarrasProporcao
                itens={dados.por_funcionario.slice(0, 12).map((f) => ({
                  rotulo: f.funcionario_nome,
                  valor: f.total_horas,
                  secundario: `${f.n_servicos} serv.`,
                }))}
              />
            </Painel>

            <Painel titulo="Horas por local" descricao="Onde o trabalho aconteceu">
              <BarrasProporcao
                itens={dados.por_local.map((l) => ({
                  rotulo: l.local_nome,
                  valor: l.total_horas,
                  secundario: `${l.n_servicos} serv.`,
                }))}
              />
            </Painel>
          </div>

          <Painel
            titulo="Serviços do mês"
            acao={<Link to="/registo-horas" className="text-xs font-medium text-marca-600 hover:underline dark:text-marca-400">Ver registos →</Link>}
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-papel-200 text-left text-[11px] uppercase tracking-wide text-tinta-500 dark:border-tinta-700 dark:text-tinta-400">
                    <th className="pb-2 pr-4 font-medium">Nº Serviço</th>
                    <th className="pb-2 pr-4 font-medium">Data</th>
                    <th className="pb-2 pr-4 font-medium">Local</th>
                    <th className="pb-2 pr-4 text-right font-medium">Funcionários</th>
                    <th className="pb-2 pr-4 text-right font-medium">Horas</th>
                    <th className="pb-2 text-right font-medium">Custo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-papel-200 dark:divide-tinta-700">
                  {dados.servicos_do_mes.map((s) => (
                    <tr key={s.numero}>
                      <td className="py-2 pr-4 font-medium tabular-nums">{s.numero}</td>
                      <td className="py-2 pr-4 tabular-nums text-tinta-600 dark:text-papel-300">{dataCurta(s.data, true)}</td>
                      <td className="py-2 pr-4 text-tinta-600 dark:text-papel-300">
                        {s.local_nome ? (
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="h-3.5 w-3.5 text-tinta-400" aria-hidden />{s.local_nome}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="py-2 pr-4 text-right tabular-nums">
                        <span className="inline-flex items-center gap-1">
                          <Users className="h-3.5 w-3.5 text-tinta-400" aria-hidden />{s.n_funcionarios}
                        </span>
                      </td>
                      <td className="py-2 pr-4 text-right tabular-nums">{horas(s.total_horas)}</td>
                      <td className="py-2 text-right tabular-nums">{euros(s.custo_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Painel>

          <Painel titulo="Últimos registos introduzidos" descricao="O que foi lançado mais recentemente">
            <ul className="divide-y divide-papel-200 text-sm dark:divide-tinta-700">
              {dados.ultimos_registos.map((r) => (
                <li key={r.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
                  <span className="font-medium">{r.funcionario_nome}</span>
                  <span className="inline-flex items-center gap-1 text-tinta-500 dark:text-tinta-400">
                    <Clock className="h-3.5 w-3.5" aria-hidden />
                    {r.hora_inicio}–{r.hora_fim}
                  </span>
                  <span className="tabular-nums text-tinta-600 dark:text-papel-300">{horas(r.total_horas)}</span>
                  <span className="tabular-nums text-tinta-500 dark:text-tinta-400">{dataCurta(r.data, true)}</span>
                  {r.local_nome && (
                    <span className="inline-flex items-center gap-1 text-tinta-500 dark:text-tinta-400">
                      <MapPin className="h-3.5 w-3.5" aria-hidden />{r.local_nome}
                    </span>
                  )}
                  <span className="ml-auto tabular-nums text-xs text-tinta-400">{r.numero_servico ?? '—'}</span>
                </li>
              ))}
            </ul>
          </Painel>
        </>
      )}
    </div>
  );
}
